import { image } from '~/js/utils/image';
import { salePrice, regPrice } from '../utils/product-item';
import { convertWishlistProduct, convertRelewiseProduct } from './product-object-converter';

export default ((varyingProduct, origin, sectionId) => {
  let product = varyingProduct;

  switch (origin) {
    case 'wishlist':
      product = convertWishlistProduct(product);
      break;

    case 'relewise':
      product = convertRelewiseProduct(product);
      break;

    default:
      break;
  }

  const uniqueId = () => {
    let id = product.handle;

    if (sectionId) {
      id += sectionId;
    }

    return id;
  };

  /**
   * Builds variant radio btns
   * @returns {TemplateLiteral}
   */
  const variantOptions = () => {
    let options = '';

    product.variants.forEach((variant) => {
      const checked = () => (variant === product.variants[0] ? 'checked' : '');
      options += `
        <div class="radio-btn ${variant.available ? '' : 'unavailable'}">
          <input
            type="radio"
            name="id"
            ${variant.available ? '' : 'disabled'}
            data-title="${variant.title}"
            data-price="${variant.price}"
            data-available="${variant.available}"
            data-old-price="${variant.compare_at_price}"
            value="${variant.id}"
            id="stl-option-${variant.id + sectionId}"
            ${checked()}
            />

            <label for="stl-option-${variant.id + sectionId}">
            ${variant.title}
          </label>
        </div>
      `;
    });

    return options;
  };

  /**
   * Builds variant selector html and status text
   * @returns {TemplateLiteral}
   */
  const variantSelector = () => {
    const variant = product.variants[0];
    const firstVariantTitle = variant.title;

    return `
      <div class="mt-5">
        <p class="text-sm">
          Variant:
          <span data-update="stl-variant">
            ${firstVariantTitle}
          </span>
        </p>
      </div>

      <div class="-m-2 flex flex-wrap items-center justify-start">
        ${variantOptions()}
      </div>
    `;
  };

  /**
   * Builds variant selector for quick actions
   * @returns {TemplateLiteral}
   */
  const variants = () => {
    const variant = product.variants[0];
    const firstVariantId = variant.id;

    if (product.variants.length > 1) {
      return variantSelector();
    }
    return `
        <input type="hidden" name="id" value="${firstVariantId}">
      `;
  };

  /**
   * Builds submit button for quick actions
   * @returns {TemplateLiteral}
   */
  const submitBtn = () => {
    const isAvailable = () => {
      const variantObj = product.variants[0];
      const { available } = variantObj;

      const availableCaptions = `
        <svg class="icon {{ svg_class }}" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="{{ path_class }}" d="M6.875 7.5625V6.1875C6.875 5.09348 7.3096 4.04427 8.08318 3.27068C8.85677 2.4971 9.90598 2.0625 11 2.0625C12.094 2.0625 13.1432 2.4971 13.9168 3.27068C14.6904 4.04427 15.125 5.09348 15.125 6.1875V7.5625M3.4375 7.5625C3.25516 7.5625 3.0803 7.63493 2.95136 7.76386C2.82243 7.8928 2.75 8.06766 2.75 8.25V17.5312C2.75 18.8306 3.85688 19.9375 5.15625 19.9375H16.8437C18.1431 19.9375 19.25 18.8843 19.25 17.585V8.25C19.25 8.06766 19.1776 7.8928 19.0486 7.76386C18.9197 7.63493 18.7448 7.5625 18.5625 7.5625H3.4375Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        ${window.theme.strings.addToCart}
      `;

      return {
        attr: available ? '' : 'disabled',
        caption: available ? availableCaptions : window.theme.strings.soldOut,
      };
    };

    return `
      <button
        class="w-full mt-6 button button--buy"
        data-update="stl-submit-button"
        data-add-to-cart-btn
        ${isAvailable().attr}>

        ${isAvailable().caption}
      </button>
    `;
  };

  const getImage = () => {
    if (!product.images.length) {
      return '';
    }

    return image({
      src: product.images[0], alt: '', width: 500, height: 500,
    }, 'image');
  };

  return `
    <article
      class="popup w-full max-w-3xl"
      role="widget"
      data-key="quick-buy-${uniqueId()}"
      data-id="${product.id}"
      data-component="quick-buy-popup">

      <div class="popup__inner">
        <section class="popup__content w-full">
          <form
            method="post"
            action="/cart/add"
            id="product_form_${product.id}"
            accept-charset="UTF-8"
            class="lg:w-5/6 lg:mx-auto"
            enctype="multipart/form-data"
            data-action="stl-submit">

            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">

            <div class="flex mb-6 align-center justify-between">
              <h3>${window.theme.strings.quickBuyPopupTitle}</h3>

              <button data-close-popup onclick="event.preventDefault();">
                <svg class="icon {{ svg_class }}" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path class="{{ path_class }}" d="M15.8125 6.1875L6.1875 15.8125M15.8125 15.8125L6.1875 6.1875L15.8125 15.8125Z" stroke="#272219" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

            <div class="flex relative mb-4">
              <div class="w-full aspect-ratio aspect-ratio--1/1">
                ${getImage()}
              </div>
            </div>

            <div class="px-4 font-heading">
              <p class="font-semibold text-base">
                ${product.vendor}
              </p>

              <p class="text-base line-clamp-2">
                <a class="no-underline text-warm-gray-500" href="${product.url}">
                  ${product.title}
                </a>
              </p>

              <p class="space-x-4 mt-4 mb-6">
                ${regPrice(product.variants[0])}
                ${salePrice(product.variants[0])}
              </p>
            </div>

            <div class="px-4 font-heading">
              ${variants()}
              ${submitBtn()}
            </div>
          </form>
        </section>
      </div>
    </article>
  `;
});
